import { Button } from "@omni/ui/components/Button/button.component";
import { useEffect } from "react";

import AreaField from "@/components/Calculator/WallpaperCalculator/areaField/areaField.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type Props = {
    calculateWallArea: (area: number) => void;
    calculateWindowArea: (area: number) => void;
    calculateDoorArea: (area: number) => void;
    calculateRollArea: (area: number) => void;
    walls: number[][];
    doors: number[][];
    windows: number[][];
    roll: number[];
    setWalls: (walls: number[][]) => void;
    setDoors: (doors: number[][]) => void;
    setWindows: (windows: number[][]) => void;
    setRoll: (roll: number[]) => void;
};

const Calculation = ({
    calculateWallArea,
    calculateWindowArea,
    calculateDoorArea,
    calculateRollArea,
    walls,
    doors,
    windows,
    roll,
    setWalls,
    setDoors,
    setWindows,
    setRoll
}: Props) => {
    const { t } = useGranite();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => calculateWallAreaTotal(), [walls]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => calculateWindowsAreaTotal(), [windows]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => calculateDoorsAreaTotal(), [doors]);

    // walls
    const calculateWallAreaTotal = () => {
        const totalArea = walls.reduce((a, b) => a + b[0] * b[1], 0);
        calculateWallArea(totalArea);
    };

    const addWallArea = (index: number, area: number[]) => {
        const wallsClone = walls.slice();
        wallsClone[index] = area;
        setWalls(wallsClone);
    };

    const addWall = () => {
        setWalls([...walls, [0, 0]]);
    };

    // windows
    const calculateWindowsAreaTotal = () => {
        const totalArea = windows.reduce((a, b) => a + b[0] * b[1], 0);
        calculateWindowArea(totalArea);
    };

    const addWindowArea = (index: number, area: number[]) => {
        const windowsClone = windows.slice();
        windowsClone[index] = area;
        setWindows(windowsClone);
    };
    const addWindow = () => {
        setWindows([...windows, [0, 0]]);
    };

    // doors
    const calculateDoorsAreaTotal = () => {
        const totalArea = doors.reduce((a, b) => a + b[0] * b[1], 0);
        calculateDoorArea(totalArea);
    };
    const addDoorArea = (index: number, area: number[]) => {
        const doorsClone = doors.slice();
        doorsClone[index] = area;
        setDoors(doorsClone);
    };
    const addDoor = () => {
        setDoors([...doors, [0, 0]]);
    };

    // rolls
    const addRollArea = (_index: number, area: number[]) => {
        setRoll(area);
        calculateRollArea(area[0] * area[1]);
    };

    return (
        <div className={"mt-4"}>
            <Row>
                <Col
                    className={"mt-4"}
                    md={6}
                >
                    <Row>
                        <Col>
                            <h3 className="d-sm-block">
                                {t(
                                    "calculator.wallpaperCalculator.wallMeasurements"
                                )}
                            </h3>
                        </Col>
                    </Row>
                    {walls.map((wall, index) => (
                        <AreaField
                            key={index}
                            id={`wall-${index}`}
                            index={index}
                            name={t("calculator.wall")}
                            updateValues={addWallArea}
                            values={wall}
                        />
                    ))}
                    {walls.length < 6 && (
                        <Row>
                            <Col>
                                <Button
                                    color="link"
                                    type="button"
                                    onClick={addWall}
                                >
                                    <i className="las la-plus-circle" />
                                    <span className={"ml-2"}>
                                        {t(
                                            "calculator.wallpaperCalculator.addWall"
                                        )}
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Col>
                <Col
                    className={"mt-4"}
                    md={6}
                >
                    <Row>
                        <Col>
                            <h3 className="d-sm-block">
                                {t(
                                    "calculator.wallpaperCalculator.windowMeasurements"
                                )}
                            </h3>
                        </Col>
                    </Row>
                    {windows.map((window, index) => (
                        <AreaField
                            key={index}
                            id={`window-${index}`}
                            index={index}
                            name={t("calculator.window")}
                            updateValues={addWindowArea}
                            values={window}
                        />
                    ))}
                    {windows.length < 6 && (
                        <Row>
                            <Col>
                                <Button
                                    color="link"
                                    type="button"
                                    onClick={addWindow}
                                >
                                    <i className="las la-plus-circle" />
                                    <span className={"ml-2"}>
                                        {t(
                                            "calculator.wallpaperCalculator.addWindow"
                                        )}
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
            <Row>
                <Col
                    className={"mt-4"}
                    md={6}
                >
                    <Row>
                        <Col>
                            <h3 className="d-sm-block">
                                {t(
                                    "calculator.wallpaperCalculator.doorMeasurements"
                                )}
                            </h3>
                        </Col>
                    </Row>
                    {doors.map((door, index) => (
                        <AreaField
                            key={index}
                            id={`door-${index}`}
                            index={index}
                            name={t("calculator.door")}
                            updateValues={addDoorArea}
                            values={door}
                        />
                    ))}
                    {doors.length < 6 && (
                        <Row>
                            <Col>
                                <Button
                                    color="link"
                                    type="button"
                                    onClick={addDoor}
                                >
                                    <i className="las la-plus-circle" />
                                    <span className={"ml-2"}>
                                        {t(
                                            "calculator.wallpaperCalculator.addDoor"
                                        )}
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Col>
                <Col
                    className={"mt-4"}
                    md={6}
                >
                    <Row>
                        <Col>
                            <h3 className="d-sm-block">
                                {t(
                                    "calculator.wallpaperCalculator.rollMeasurements"
                                )}
                            </h3>
                        </Col>
                    </Row>
                    <AreaField
                        id={"roll"}
                        updateValues={addRollArea}
                        index={0}
                        isLength={true}
                        values={roll}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default Calculation;
